import { useEffect, useState, type ReactNode } from 'react'
import { api } from '@/api'
import { ApiError } from '@/api/client'
import type { CoinLedgerItem, Identity } from '@/api'
import { LedgerTable } from '@/components/LedgerTable'
import { useAuth } from '@/context/AuthContext'
import { Badge, Button, Card, CardBody, CardHead, Stat } from '@/components/ui'

/**
 * 账号中心（`/account`）。
 *
 * 2026-10-07 从顶栏那个账号下拉里「点开 → 新页面」升上来的。原先是两个弹层
 * （额度账本 / 退出确认），弹层的毛病在账本上最明显：**50 条记录挤在 52vh 的
 * 小框里，还要在内滚动条里翻** —— 而账本是用户拿去对账的东西，对账要能一眼看到
 * 一段时间内的所有进出。升成整页之后，表格跟着页面滚，想翻多长翻多长。
 *
 * ### 三条口径
 *
 * 1. **余额只有一个来源**：`/api/me` 的 `coinBalance`。顶栏那枚胶囊走的是
 *    `/api/auth/session`（契约 `AuthUser.coins`）—— 两个接口的字段名不同是历史
 *    原因，但**界面上不会出现两个数字**：本页只读 `me()`，顶栏只读 `session()`，
 *    各自刷新自己的那份，不互相抄。
 * 2. **「已用额度」是净额**（扣费 − 退款），来自后端按 `kind` 累计，前端不做减法。
 *    前端算的话，「初始 − 剩余」会把管理员调整额度也算成"用掉的"，数字只会偏大。
 * 3. **未登录进不来**：本页在 `RequireAuth` 里面，`user` 为空时根本不会被渲染。
 *    所以下面所有 `user?.` 都是类型收窄，不是运行时分支。
 *
 * ### 🔴 这一页**没有**「后台管理」入口了（2026-10-07 拆表）
 *
 * 原先这里有一个「账号角色」格（显示管理员/普通成员）+ 一颗「后台管理」按钮，
 * 两者都由 `user.role` 驱动。拆表后会员表上**没有 role 这个字段**，
 * 后台账号住在 `admins` 表、走另一套登录 —— 所以：
 *
 *   · `isAdmin` 恒为 false ⇒ 那颗按钮对**每一个**打开这一页的人都不显示，
 *     是纯粹的死代码（比不显示更坏：读代码的人以为这里有后台入口）；
 *   · 「账号角色」格换成「套餐」（`Identity.plan`）—— 那才是会员侧真有的字段。
 *
 * 后台入口现在**只能敲 `/admin`**（见 config/nav.ts 的 HeaderEntry 段：
 * 顶栏那条对每个会员都是死链，所以删了）。这不是疏漏 ——
 * 会员与后台是两套账号，给会员摆一个「后台管理」按钮就是摆一条走不通的路。
 */
export function Account() {
  const { user, logout } = useAuth()

  const [ident, setIdent] = useState<Identity | null>(null)
  const [items, setItems] = useState<CoinLedgerItem[] | null>(null)
  const [err, setErr] = useState('')
  const [ledgerErr, setLedgerErr] = useState('')

  useEffect(() => {
    let alive = true
    api
      .me()
      .then((d) => alive && setIdent(d))
      .catch((e) => alive && setErr(e instanceof ApiError ? e.message : '读取账号信息失败'))
    // 200 条是后端的默认上限：账本条目只增不减，一次性全拉会在几个月后变成一屏几万行。
    api
      .myLedger(200)
      .then((d) => alive && setItems(d))
      .catch((e) => alive && setLedgerErr(e instanceof ApiError ? e.message : '读取账本失败'))
    return () => {
      alive = false
    }
  }, [])

  // 余额优先用 `me()` 的那份（它和下面的账本出自同一个时刻）；还没回来时先用
  // 登录态里的数字顶上 —— 显示成「—」会让用户以为额度丢了。
  const balance = ident ? ident.coinBalance : user?.coins

  return (
    <div className="page">
      <Card>
        <CardHead title="账号概览" icon="users" />
        <CardBody>
          {err ? <div className="admin__error">{err}</div> : null}
          <div className="account__stats">
            <Stat
              label="可用额度"
              value={balance === undefined ? '—' : balance.toFixed(1)}
              suffix="积分"
              icon="coin"
            />
            <Stat
              label="已用额度"
              value={ident ? ident.coinUsed.toFixed(1) : '—'}
              suffix="积分"
              icon="bolt"
              delta={ident ? '扣费 − 退款' : undefined}
              deltaTone="flat"
            />
            <Stat
              label="套餐"
              value={ident?.plan || '—'}
              icon="sparkles"
              delta={user?.account}
              deltaTone="flat"
            />
          </div>

          <div className="account__rows">
            <Row label="登录账号" value={user?.account ?? '—'} />
            <Row label="昵称" value={user?.displayName || '—'} />
            <Row
              label="账号状态"
              value={
                user?.status === 'disabled' ? (
                  <Badge tone="danger">已停用</Badge>
                ) : (
                  <Badge tone="success">正常</Badge>
                )
              }
            />
            <Row
              label="创建时间"
              value={user?.createdAt ? new Date(user.createdAt).toLocaleString() : '—'}
            />
          </div>

          <div className="account__actions">
            {/* 「后台管理」按钮已删 —— 会员账号进不去后台（见文件头部那段）。
                这里不给「敲 /admin」的提示：本页每个访客都是会员，
                对他们来说那是一句做不到的建议。 */}
            <Button variant="ghost" icon="x" onClick={() => void logout()}>
              退出登录
            </Button>
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHead title="额度账本" icon="coin" />
        <CardBody>
          {/* 卡头那行说明已下线；账本读取失败改在正文里报，避免静默丢失 */}
          {ledgerErr ? <div className="admin__error">{ledgerErr}</div> : null}
          {items ? (
            <LedgerTable items={items} />
          ) : (
            <div className="row text-sm muted" style={{ gap: 8 }}>
              <span className="spinner" /> 正在读取账本…
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  )
}

/** 一行「标签 : 值」。值可以是字符串，也可以是状态徽标。 */
function Row({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="account__row">
      <span className="account__row-label">{label}</span>
      <span className="account__row-value">{value}</span>
    </div>
  )
}
